import React, { useEffect, useState } from 'react';

const STORAGE_KEY = 'delivery-sidebar-collapsed';

// Inline stroke icons (24x24, stroke 1.5) — matched to the People portal's visual language.
const Icon = ({ path, paths }) => (
  <svg
    className="portal-sidebar-icon"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {paths ? paths.map((d, i) => <path key={i} d={d} />) : <path d={path} />}
  </svg>
);

const ICONS = {
  home: <Icon paths={['M3 10.5 12 3l9 7.5', 'M5 10v10a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V10']} />,
  schedule: <Icon paths={['M8 2v4', 'M16 2v4', 'M3 10h18', 'M5 6h14a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z']} />,
  packets: <Icon paths={['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M8 13h8', 'M8 17h8']} />,
  calculator: <Icon paths={['M4 3h16a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z', 'M8 7h8', 'M8 12h.01', 'M12 12h.01', 'M16 12h.01', 'M8 16h.01', 'M12 16h.01', 'M16 16h.01']} />,
  expenses: <Icon paths={['M4 3h12l4 4v14H4z', 'M8 8h6', 'M8 12h8', 'M8 16h5']} />,
  comparison: <Icon paths={['M3 3v18h18', 'M7 15l4-4 3 3 5-6']} />,
  guidelines: <Icon paths={['M4 5a2 2 0 0 1 2-2h13v18H6a2 2 0 0 1-2-2z', 'M9 3v18', 'M13 8h3', 'M13 12h3']} />,
  admin: <Icon paths={['M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-2.82 1.17V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3.6 14a1.65 1.65 0 0 0-1.51-1H2a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 3.6 8a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8 3.6 1.65 1.65 0 0 0 9 2.09V2a2 2 0 0 1 4 0v.09A1.65 1.65 0 0 0 16 3.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 20.4 8a1.65 1.65 0 0 0 1.51 1H22a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z']} />,
};

const PanelIcon = ({ collapsed }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <line x1="9" y1="4" x2="9" y2="20" />
    {collapsed && <polyline points="13 9 16 12 13 15" />}
    {!collapsed && <polyline points="16 9 13 12 16 15" />}
  </svg>
);

function Sidebar({ items, activeTab, onSelect, mobileOpen, onMobileClose }) {
  const [collapsed, setCollapsed] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    try {
      setCollapsed(localStorage.getItem(STORAGE_KEY) === '1');
    } catch (e) {
      // ignore storage errors
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const persistCollapsed = (next) => {
    setCollapsed(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch (e) {
      // ignore storage errors
    }
  };

  // Icon-rail collapse is desktop-only; mobile uses the overlay drawer.
  const railCollapsed = collapsed && !isMobile;

  const shellClass = [
    'portal-sidebar',
    railCollapsed ? 'is-collapsed' : '',
    mobileOpen ? 'is-mobile-open' : '',
    hydrated ? 'is-hydrated' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleSelect = (key) => {
    onSelect(key);
    if (onMobileClose) onMobileClose();
  };

  return (
    <>
      <div
        className={`portal-sidebar-backdrop${mobileOpen ? ' is-visible' : ''}`}
        onClick={onMobileClose}
        aria-hidden={!mobileOpen}
      />
      <aside className={shellClass} aria-label="Primary">
        <div className="portal-sidebar-top">
          <button
            type="button"
            className="portal-sidebar-collapse"
            aria-label={railCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={railCollapsed ? 'Expand' : 'Collapse'}
            onClick={() => persistCollapsed(!railCollapsed)}
          >
            <PanelIcon collapsed={railCollapsed} />
          </button>
        </div>
        <nav className="portal-sidebar-nav">
          <ul className="portal-sidebar-list">
            {items.map((item) => {
              const active = activeTab === item.key;
              return (
                <li key={item.key}>
                  <button
                    type="button"
                    className={`portal-sidebar-link${active ? ' is-active' : ''}`}
                    onClick={() => handleSelect(item.key)}
                    title={railCollapsed ? item.label : undefined}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span className="portal-sidebar-link-icon">{ICONS[item.key]}</span>
                    <span className="portal-sidebar-link-label">{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    </>
  );
}

export default Sidebar;
